import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { SECTORS, STAGES, RISK_CONFIG } from './pipelineData';
import { ROUTES } from 'config/navConfig';
import { MOCK_FUNDS } from 'data/mockData';

const PAGE_SIZE = 10;

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <Icon name="ChevronsUpDown" size={12} color="#94a3b8" />;
  return sortDir === 'asc' ? <Icon name="ChevronUp" size={12} color="#2563eb" /> : <Icon name="ChevronDown" size={12} color="#2563eb" />;
}

export default function PipelineTable({ deals, onMoveStage, onToggleFlag, onAddToast }) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('dateAdded');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sorted = useMemo(() => {
    return [...(deals || [])]?.sort((a, b) => {
      let av = a?.[sortField];
      let bv = b?.[sortField];
      if (typeof av === 'string') av = av?.toLowerCase();
      if (typeof bv === 'string') bv = bv?.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [deals, sortField, sortDir]);

  const totalPages = Math.ceil((sorted?.length || 0) / PAGE_SIZE);
  const paginated = sorted?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const Th = ({ field, label }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 whitespace-nowrap"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
      </span>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th field="name" label="Deal Name" />
              <Th field="sector" label="Sector" />
              <Th field="region" label="Region" />
              <Th field="stage" label="Stage" />
              <Th field="targetIRR" label="Target IRR" />
              <Th field="ticketSize" label="Ticket" />
              <Th field="riskRating" label="Risk" />
              <Th field="dateAdded" label="Date Added" />
              <Th field="daysInStage" label="Days in Stage" />
              <Th field="assignedName" label="Assigned To" />
              <Th field="lastActivity" label="Last Activity" />
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginated?.map((deal) => {
              const sector = SECTORS?.find((s) => s?.id === deal?.sector);
              const stage = STAGES?.find((s) => s?.id === deal?.stage);
              const risk = RISK_CONFIG?.[deal?.riskRating];
              const currentIdx = STAGES?.findIndex((s) => s?.id === deal?.stage);
              const adjacentStages = STAGES?.filter((_, idx) => idx === currentIdx - 1 || idx === currentIdx + 1);

              return (
                <tr key={deal?.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(ROUTES.dealRecord.replace(':id', deal?.id))}
                      className="font-semibold text-slate-800 text-sm hover:text-[#3b4c4c] transition-colors text-left flex items-center gap-1.5"
                    >
                      {deal?.flagged && <Icon name="Flag" size={12} color="#f59e0b" />}
                      {deal?.name}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {sector && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                          style={{ color: sector?.color, background: sector?.bg }}
                        >
                          {sector?.label}
                        </span>
                      )}
                      {deal?.fundId && (
                        <button
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap transition-opacity hover:opacity-75"
                          style={{ background: 'var(--color-primary-subtle)', color: 'var(--color-primary)' }}
                          onClick={(e) => { e.stopPropagation(); navigate(ROUTES.fundDetail.replace(':id', deal.fundId)); }}
                        >
                          <Icon name="Landmark" size={10} color="var(--color-primary)" />
                          {MOCK_FUNDS.find(f => f.id === deal.fundId)?.shortName ?? deal.fundId}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{deal?.region}</td>
                  <td className="px-4 py-3">
                    {stage && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap"
                        style={{ color: stage?.color, background: stage?.bg, borderColor: stage?.border }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage?.color }} />
                        {stage?.label}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-800">{deal?.targetIRR}%</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-800">{String.fromCharCode(163)}{deal?.ticketSize}m</td>
                  <td className="px-4 py-3">
                    {risk && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                        style={{ color: risk?.color, background: risk?.bg, borderColor: risk?.border }}
                      >
                        {deal?.riskRating}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                    {new Date(deal?.dateAdded)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium ${
                      deal?.daysInStage > 60 ? 'text-red-600' : deal?.daysInStage > 30 ? 'text-amber-600' : 'text-slate-700'
                    }`}>
                      {deal?.daysInStage}d
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: 'var(--color-primary, #3b4c4c)' }}
                      >
                        {deal?.assignedTo}
                      </div>
                      <span className="text-sm text-slate-700 whitespace-nowrap">{deal?.assignedName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                    {new Date(deal?.lastActivity)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigate(ROUTES.dealRecord.replace(':id', deal?.id))}
                        className="p-1.5 rounded text-slate-400 hover:text-[#3b4c4c] hover:bg-[rgba(59,76,76,0.07)] transition-colors"
                        title="View"
                      >
                        <Icon name="Eye" size={14} />
                      </button>
                      <div className="relative group/move">
                        <button
                          className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          title="Move stage"
                        >
                          <Icon name="ArrowRightLeft" size={14} />
                        </button>
                        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-40 py-1 hidden group-hover/move:block">
                          {adjacentStages?.map((s) => (
                            <button
                              key={s?.id}
                              onClick={() => {
                                onMoveStage?.(deal?.id, s?.id);
                                onAddToast?.({ type: 'success', title: 'Deal moved', message: deal?.name + ' moved to ' + s?.label });
                              }}
                              className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                            >
                              <span className="w-2 h-2 rounded-full" style={{ background: s?.color }} />
                              {s?.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => onToggleFlag?.(deal?.id)}
                        className={`p-1.5 rounded transition-colors ${
                          deal?.flagged ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'
                        }`}
                        title={deal?.flagged ? 'Remove flag' : 'Flag'}
                      >
                        <Icon name="Flag" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500">
            Showing {(page - 1) * PAGE_SIZE + 1}&#8211;{Math.min(page * PAGE_SIZE, sorted?.length)} of {sorted?.length} deals
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                  p === page ? 'bg-[#3b4c4c] text-white' : 'text-slate-600 hover:bg-slate-200'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
